/**
 * Pipeline Onboarding Tour Composable
 * UX/UI Sprint - Task B3: Onboarding Tour
 *
 * Provides guided tour functionality for new Pipeline users.
 * Uses localStorage to persist tour completion status.
 */

import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

// localStorage key for tour state
const TOUR_STORAGE_KEY = 'pipeline_tour_completed';
const TOUR_DISMISSED_KEY = 'pipeline_tour_dismissed';

/**
 * Tour step configuration
 * Each step targets a specific element and provides guidance
 */
export const getTourSteps = t => [
  {
    id: 'welcome',
    target: null, // Center of screen, no specific target
    title: t('PIPELINE.TOUR.STEPS.WELCOME.TITLE'),
    content: t('PIPELINE.TOUR.STEPS.WELCOME.CONTENT'),
    position: 'center',
    showSkip: true,
  },
  {
    id: 'columns',
    target: '.pipeline-column-wrapper',
    title: t('PIPELINE.TOUR.STEPS.COLUMNS.TITLE'),
    content: t('PIPELINE.TOUR.STEPS.COLUMNS.CONTENT'),
    position: 'bottom',
    highlight: true,
  },
  {
    id: 'add-card',
    target: '[data-tour="add-card-button"]',
    title: t('PIPELINE.TOUR.STEPS.ADD_CARD.TITLE'),
    content: t('PIPELINE.TOUR.STEPS.ADD_CARD.CONTENT'),
    position: 'left',
    highlight: true,
  },
  {
    id: 'drag-drop',
    target: '.pipeline-card',
    title: t('PIPELINE.TOUR.STEPS.DRAG_DROP.TITLE'),
    content: t('PIPELINE.TOUR.STEPS.DRAG_DROP.CONTENT'),
    position: 'right',
    highlight: true,
  },
  {
    id: 'filters',
    target: '[data-tour="filter-button"]',
    title: t('PIPELINE.TOUR.STEPS.FILTERS.TITLE'),
    content: t('PIPELINE.TOUR.STEPS.FILTERS.CONTENT'),
    position: 'bottom',
    highlight: true,
  },
  {
    id: 'keyboard',
    target: null,
    title: t('PIPELINE.TOUR.STEPS.KEYBOARD.TITLE'),
    content: t('PIPELINE.TOUR.STEPS.KEYBOARD.CONTENT'),
    position: 'center',
    showSkip: false,
  },
  {
    id: 'complete',
    target: null,
    title: t('PIPELINE.TOUR.STEPS.COMPLETE.TITLE'),
    content: t('PIPELINE.TOUR.STEPS.COMPLETE.CONTENT'),
    position: 'center',
    isLast: true,
  },
];

export const usePipelineTour = () => {
  const { t } = useI18n();

  // Tour state
  const isActive = ref(false);
  const currentStepIndex = ref(0);
  const steps = ref([]);
  const targetElement = ref(null);
  const targetRect = ref(null);

  // Computed
  const currentStep = computed(
    () => steps.value[currentStepIndex.value] || null
  );
  const totalSteps = computed(() => steps.value.length);
  const progress = computed(
    () => ((currentStepIndex.value + 1) / totalSteps.value) * 100
  );
  const isFirstStep = computed(() => currentStepIndex.value === 0);
  const isLastStep = computed(
    () => currentStepIndex.value === totalSteps.value - 1
  );

  // Check if tour should be shown
  const shouldShowTour = computed(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
    const dismissed = localStorage.getItem(TOUR_DISMISSED_KEY) === 'true';
    return !completed && !dismissed;
  });

  // Initialize steps
  const initSteps = () => {
    steps.value = getTourSteps(t);
  };

  // Find and highlight target element
  const updateTargetElement = async () => {
    await nextTick();

    const step = currentStep.value;
    if (!step || !step.target) {
      targetElement.value = null;
      targetRect.value = null;
      return;
    }

    const element = document.querySelector(step.target);
    if (element) {
      targetElement.value = element;
      targetRect.value = element.getBoundingClientRect();

      // Scroll element into view if needed
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      targetElement.value = null;
      targetRect.value = null;
      // eslint-disable-next-line no-console
      console.warn(
        `[PipelineTour] Target not found for step "${step.id}": ${step.target}. Falling back to center.`
      );
    }
  };

  // End the tour (cleanup)
  const endTour = () => {
    isActive.value = false;
    currentStepIndex.value = 0;
    targetElement.value = null;
    targetRect.value = null;

    // Remove body class
    document.body.classList.remove('tour-active');
  };

  // Complete the tour
  const completeTour = () => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    endTour();
  };

  // Skip/dismiss the tour
  const skipTour = () => {
    localStorage.setItem(TOUR_DISMISSED_KEY, 'true');
    endTour();
  };

  // Start the tour
  const startTour = () => {
    initSteps();
    currentStepIndex.value = 0;
    isActive.value = true;
    updateTargetElement();

    // Add body class to prevent scrolling
    document.body.classList.add('tour-active');
  };

  // Go to next step
  const nextStep = () => {
    if (currentStepIndex.value < totalSteps.value - 1) {
      currentStepIndex.value += 1;
      updateTargetElement();
    } else {
      completeTour();
    }
  };

  // Go to previous step
  const prevStep = () => {
    if (currentStepIndex.value > 0) {
      currentStepIndex.value -= 1;
      updateTargetElement();
    }
  };

  // Go to specific step
  const goToStep = index => {
    if (index >= 0 && index < totalSteps.value) {
      currentStepIndex.value = index;
      updateTargetElement();
    }
  };

  // Reset tour (for testing or re-showing)
  const resetTour = () => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    localStorage.removeItem(TOUR_DISMISSED_KEY);
  };

  // Handle window resize
  const handleResize = () => {
    if (isActive.value && targetElement.value) {
      targetRect.value = targetElement.value.getBoundingClientRect();
    }
  };

  // Handle keyboard navigation
  const handleKeydown = event => {
    if (!isActive.value) return;

    // Don't intercept keys when user is typing in form fields
    const eventTarget = event.target;
    const isFormField =
      eventTarget instanceof HTMLElement &&
      (eventTarget.tagName === 'INPUT' ||
        eventTarget.tagName === 'TEXTAREA' ||
        eventTarget.tagName === 'SELECT' ||
        eventTarget.isContentEditable);
    if (isFormField) return;

    switch (event.key) {
      case 'Escape':
        skipTour();
        break;
      case 'ArrowRight':
      case 'Enter':
        nextStep();
        break;
      case 'ArrowLeft':
        prevStep();
        break;
      default:
        break;
    }
  };

  // Lifecycle
  onMounted(() => {
    window.addEventListener('resize', handleResize);
    window.addEventListener('keydown', handleKeydown);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('keydown', handleKeydown);
    document.body.classList.remove('tour-active');
  });

  return {
    // State
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    progress,
    isFirstStep,
    isLastStep,
    targetElement,
    targetRect,
    shouldShowTour,

    // Actions
    startTour,
    nextStep,
    prevStep,
    goToStep,
    skipTour,
    completeTour,
    resetTour,
  };
};

export default usePipelineTour;
